/**
 * IndexedDB wrapper for QDA app
 * Mirrors plugin's DBClass behavior
 * DB: "QdaAppDB", Store: "qda-store", Version: 1
 */

import { openDB, DBSchema, IDBPDatabase } from "idb";
import { StoredValue } from "../types";

export interface QdaSchema extends DBSchema {
  "qda-store": {
    key: string;
    value: StoredValue<any>;
  };
}

class DBClass {
  private db: IDBPDatabase<QdaSchema> | null = null;

  async init(): Promise<void> {
    this.db = await openDB<QdaSchema>("QdaAppDB", 1, {
      upgrade(db) {
        if (!db.objectStoreNames.contains("qda-store")) {
          db.createObjectStore("qda-store");
        }
      },
    });
  }

  private async ensureDb(): Promise<IDBPDatabase<QdaSchema>> {
    if (!this.db) {
      await this.init();
    }
    return this.db!;
  }

  /**
   * Get an item from the store, with support for legacy records (no modifiedTime wrapper)
   */
  async getItem(key: string): Promise<StoredValue<any> | null> {
    const db = await this.ensureDb();
    const item = await db.get("qda-store", key);

    if (!item) {
      return null;
    }

    // Support legacy records without wrapper
    if (item.value !== undefined && item.modifiedTime !== undefined) {
      return item as StoredValue<any>;
    }

    // Legacy record: wrap it
    return {
      value: item,
      modifiedTime: new Date().toISOString(),
      synced: false,
    };
  }

  /**
   * Set an item in the store
   */
  async setItem(key: string, value: any, modifiedTime?: string): Promise<void> {
    const db = await this.ensureDb();
    const now = new Date().toISOString();
    const stored: StoredValue<any> = {
      value: cleanData(value),
      modifiedTime: modifiedTime || now,
      synced: !!modifiedTime,
    };
    await db.put("qda-store", stored, key);
  }

  /**
   * Remove a single item
   */
  async removeItem(key: string): Promise<void> {
    const db = await this.ensureDb();
    await db.delete("qda-store", key);
  }

  /**
   * Remove multiple items
   */
  async removeItems(keys: string[]): Promise<void> {
    const db = await this.ensureDb();
    await Promise.all(keys.map((key) => db.delete("qda-store", key)));
  }

  /**
   * Get all keys in the store
   */
  async getKeys(): Promise<string[]> {
    const db = await this.ensureDb();
    return db.getAllKeys("qda-store");
  }

  /**
   * Get all keys matching a prefix
   */
  async getKeysByPrefix(prefix: string): Promise<string[]> {
    const allKeys = await this.getKeys();
    return allKeys.filter((key) => key.startsWith(prefix));
  }

  /**
   * Get all items under a project prefix as a map
   */
  async getAll(projectName: string): Promise<Record<string, any>> {
    const prefix = `${projectName}/`;
    const keys = await this.getKeysByPrefix(prefix);
    const result: Record<string, any> = {};

    await Promise.all(
      keys.map(async (key) => {
        const item = await this.getItem(key);
        if (item) {
          result[key] = item.value;
        }
      })
    );

    return result;
  }

  /**
   * Get all unique project names
   */
  async allProjects(): Promise<string[]> {
    const keys = await this.getKeys();
    const projects = new Set<string>();
    for (const key of keys) {
      const parts = key.split("/");
      if (parts.length >= 1) {
        projects.add(parts[0]);
      }
    }
    return Array.from(projects).sort();
  }

  /**
   * Clear all items from the store
   */
  async clear(): Promise<void> {
    const db = await this.ensureDb();
    await db.clear("qda-store");
  }

  /**
   * Get all keys and values (for debugging/export)
   */
  async dumpAll(): Promise<Record<string, any>> {
    const keys = await this.getKeys();
    const result: Record<string, any> = {};

    await Promise.all(
      keys.map(async (key) => {
        const item = await this.getItem(key);
        if (item) {
          result[key] = item.value;
        }
      })
    );

    return result;
  }
}

/**
 * Clean data before storage: remove functions, symbols, etc.
 * Preserve Blob, ArrayBuffer, typed arrays (IndexedDB structured clone)
 */
function cleanData(obj: any): any {
  if (obj === null || typeof obj !== "object") {
    // Remove functions and symbols
    if (typeof obj === "function" || typeof obj === "symbol") {
      return undefined;
    }
    return obj;
  }

  // Preserve Blob and typed arrays
  if (obj instanceof Blob || ArrayBuffer.isView(obj) || obj instanceof ArrayBuffer) {
    return obj;
  }

  // Handle arrays
  if (Array.isArray(obj)) {
    return obj.map((item) => cleanData(item)).filter((item) => item !== undefined);
  }

  // Handle objects
  const cleaned: any = {};
  for (const [key, value] of Object.entries(obj)) {
    const cleaned_value = cleanData(value);
    if (cleaned_value !== undefined) {
      cleaned[key] = cleaned_value;
    }
  }
  return cleaned;
}

export const db = new DBClass();
